import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Filter values that live in the URL query string, so they survive reloads,
 * shared links and returning to the page. Values equal to their default are
 * left out of the URL. Other query parameters are kept as they are.
 */
export default function useUrlFilters(defaults) {
  const history = useHistory();
  const location = useLocation();
  const keys = Object.keys(defaults).join(',');

  const values = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [key, params.get(key) ?? fallback]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, keys]);

  const setValues = useCallback(
    (next) => {
      const params = new URLSearchParams(location.search);
      Object.entries(defaults).forEach(([key, fallback]) => {
        const value = next[key];
        if (value === undefined || value === null || value === '' || value === fallback) params.delete(key);
        else params.set(key, value);
      });
      const search = params.toString();
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, location.pathname, location.search, keys],
  );

  return [values, setValues];
}
